import { env } from "@/config/env";
import { briefSchema, draftSchema, organizeSchema, seoSchema } from "@/shared/blog/ai";
import { planInternalLinks, type LinkTarget } from "@/shared/blog/internalLinks";
import { authenticatedSSEFetch, sseOnOpen } from "@/utils/sseAuth";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import type { z } from "zod";
import type {
  AiResult,
  AiStageEvent,
  AiUsage,
  BlogAiService,
  BriefInput,
  DraftInput,
  LinksInput,
  OrganizeInput,
  SeoInput,
} from "./blogAiService.contract";

/**
 * Gerador real (E5.3): fala com os 4 endpoints de IA do notifyme por SSE.
 *
 * Streaming e não requisição comum porque a redação de um post leva dezenas de
 * segundos — sem stream, o autor olharia para um spinner sem saber se está
 * funcionando, e a requisição ainda correria risco de estourar timeout.
 *
 * A chave da Anthropic nunca chega aqui: quem chama o modelo é o backend.
 * Este módulo só transporta e VALIDA — a saída de um modelo é entrada não
 * confiável, e o schema é a fronteira.
 */

const BASE = `${env.API_URL.replace(/\/$/, "")}/v1/backoffice/blog/ai`;

/** Formato dos eventos SSE, documentado em docs/blog/api-contracts.md. */
interface ServerEvent {
  type: "status" | "delta" | "result" | "error";
  status?: string;
  delta?: string;
  result?: unknown;
  usage?: { input_tokens?: number; output_tokens?: number; model?: string };
  message?: string;
}

// O genérico infere do schema (`z.infer`), não o contrário: campos com
// `.default()` têm tipo de ENTRADA diferente do de SAÍDA, e amarrar em
// `z.ZodType<T>` pegaria o de entrada — onde `h3?` é opcional — quebrando a
// compatibilidade com `BriefOutput`, em que ele já é obrigatório.
/**
 * Mensagem para quem está no editor. O JSON cru do Zod não diz nada a um
 * autor; os campos com problema ajudam quem for investigar.
 */
const describeInvalidOutput = (error: z.ZodError): string => {
  const fields = [...new Set(error.issues.map(issue => issue.path.join(".") || "resposta"))];
  return (
    "A IA devolveu uma resposta fora do formato esperado. Tente de novo — " +
    `se repetir, avise o time (campos: ${fields.join(", ")}).`
  );
};

const runStage = async <S extends z.ZodTypeAny>(
  stage: "organize" | "brief" | "draft" | "seo",
  body: unknown,
  schema: S,
  onEvent: (event: AiStageEvent) => void,
  signal?: AbortSignal
): Promise<AiResult<z.infer<S>>> => {
  let result: unknown;
  let usage: AiUsage | null = null;
  let failure: string | null = null;

  await fetchEventSource(`${BASE}/${stage}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    fetch: authenticatedSSEFetch,
    onopen: sseOnOpen,
    signal,
    // Geração de post é uma tarefa que o autor inicia e acompanha; trocar de
    // aba no meio não pode abortá-la.
    openWhenHidden: true,
    onmessage({ data }) {
      if (!data) return;
      let event: ServerEvent;
      try {
        event = JSON.parse(data) as ServerEvent;
      } catch {
        return; // keep-alive ou lixo: ignora, não derruba o stream.
      }

      if (event.type === "status" && event.status) onEvent({ status: event.status });
      if (event.type === "delta" && event.delta) onEvent({ delta: event.delta });
      if (event.type === "error") failure = event.message ?? "Falha na geração.";
      if (event.type === "result") {
        result = event.result;
        if (event.usage) {
          usage = {
            inputTokens: event.usage.input_tokens ?? 0,
            outputTokens: event.usage.output_tokens ?? 0,
            model: event.usage.model ?? "desconhecido",
          };
        }
      }
    },
    onerror(error) {
      // Sem retry automático: repetir uma geração custa dinheiro e pode
      // duplicar o trabalho. Quem decide tentar de novo é a pessoa.
      throw error;
    },
  });

  if (failure) throw new Error(failure);
  if (result === undefined) throw new Error("O servidor encerrou sem devolver resultado.");

  // Validação, não formalidade: sem isso, um campo faltando viraria `undefined`
  // silencioso dentro do editor.
  const parsed = schema.safeParse(result);
  if (!parsed.success) throw new Error(describeInvalidOutput(parsed.error));
  return { output: parsed.data, usage };
};

/**
 * Corpo no formato de `RequestBlogAi*` do notifyme: snake_case (a API usa
 * `JsonSnakeCaseNamingPolicy` também na entrada), cluster em campos soltos e
 * o briefing como STRING com o JSON. Mandar o objeto camelCase direto fazia o
 * backend não casar campo nenhum — o briefing saía com título, palavra-chave
 * e playbook vazios, e o rascunho levava 400 (`brief` objeto não vira string).
 */
const toApiContext = (input: BriefInput) => ({
  topic_title: input.topicTitle,
  primary_keyword: input.primaryKeyword,
  cluster_name: input.cluster?.name ?? null,
  cluster_head_keyword: input.cluster?.headKeyword ?? null,
  target_landing_path: input.cluster?.targetLandingPath ?? null,
  pillar_title: input.pillarTitle,
  sibling_titles: input.siblingTitles,
  playbook: input.playbook,
  topic_angle: input.topic?.angle ?? null,
  search_intent: input.topic?.searchIntent ?? null,
  funnel: input.topic?.funnel ?? null,
});

const generateOrganize = (
  input: OrganizeInput,
  onEvent: (event: AiStageEvent) => void,
  signal?: AbortSignal
) =>
  runStage(
    "organize",
    { ...toApiContext(input), excerpt: input.excerpt },
    organizeSchema,
    onEvent,
    signal
  );

const generateBrief = (
  input: BriefInput,
  onEvent: (event: AiStageEvent) => void,
  signal?: AbortSignal
) => runStage("brief", toApiContext(input), briefSchema, onEvent, signal);

const generateDraft = (
  input: DraftInput,
  onEvent: (event: AiStageEvent) => void,
  signal?: AbortSignal
) =>
  runStage(
    "draft",
    { ...toApiContext(input), brief: JSON.stringify(input.brief) },
    draftSchema,
    onEvent,
    signal
  );

const generateSeo = (
  input: SeoInput,
  onEvent: (event: AiStageEvent) => void,
  signal?: AbortSignal
) =>
  runStage(
    "seo",
    {
      primary_keyword: input.brief.primaryKeyword,
      brief: JSON.stringify(input.brief),
      content_html: input.contentHtml,
      playbook: input.playbook,
    },
    seoSchema,
    onEvent,
    signal
  );

/**
 * Links internos rodam NO CLIENTE mesmo no serviço real: o motor é
 * determinístico e puro (`internalLinks.ts`), então mandá-lo ao servidor só
 * acrescentaria latência e um lugar a mais para as duas implementações
 * divergirem. O backend usa o mesmo algoritmo no job mensal (E7.2).
 */
const planLinks = async (input: LinksInput) => {
  const targets: LinkTarget[] = [];

  if (input.cluster) {
    targets.push({
      // `resolveHref` trata `role: "landing"` como caminho pronto — tirar a
      // barra geraria `href="crediario"`, relativo, que de dentro de um post
      // resolve para /blog/crediario (404). E quebraria a idempotência:
      // `extractInternalLinks` só reconhece href começando com "/", então
      // cada nova execução acrescentaria outro bloco de CTA.
      slug: input.cluster.targetLandingPath,
      title: input.cluster.name,
      keywords: [input.cluster.headKeyword, input.cluster.name],
      role: "landing",
    });
  }
  if (input.pillar) {
    targets.push({
      slug: input.pillar.slug,
      title: input.pillar.title,
      keywords: [input.pillar.title],
      role: "pillar",
    });
  }
  for (const s of input.siblings) {
    targets.push({ slug: s.slug, title: s.title, keywords: [s.title], role: "satellite" });
  }

  return planInternalLinks(input.contentHtml, targets, { dryRun: input.dryRun });
};

export const httpBlogAiService: BlogAiService = {
  generateOrganize,
  generateBrief,
  generateDraft,
  generateSeo,
  planLinks,
};
